import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Warning, Calendar, Check } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const AgeVerificationPage = () => {
  const navigate = useNavigate();
  const { verifyAge, isAgeVerified } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // If already verified, redirect
  React.useEffect(() => {
    if (isAgeVerified) {
      navigate('/feed');
    }
  }, [isAgeVerified, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!dateOfBirth) {
      toast.error('Please enter your date of birth');
      return;
    }
    
    if (!agreeToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setLoading(true);
    try {
      await verifyAge(dateOfBirth, agreeToTerms);
      toast.success('Age verified successfully!');
      navigate('/feed');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-[#09090b] flex items-center justify-center p-6"
      style={{
        backgroundImage: `url('https://images.pexels.com/photos/7717491/pexels-photo-7717491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-[#09090b]/90 backdrop-blur-sm" />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md glass rounded-3xl p-8"
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
            <ShieldCheck weight="duotone" className="w-10 h-10 text-blue-500" />
          </div>
        </div>
        
        {/* Title */}
        <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
          Age Verification Required
        </h1>
        <p className="text-slate-400 text-center mb-8">
          This platform contains adult content. You must be 18 years or older to continue.
        </p>
        
        {/* Warning */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Warning weight="fill" className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200">
              This site contains sexually explicit material intended for adults only. 
              By entering, you confirm you are at least 18 years old.
            </p>
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={handleVerify} className="space-y-6">
          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Date of Birth
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                className="w-full bg-slate-900 border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                data-testid="dob-input"
              />
            </div>
          </div>
          
          {/* Terms Checkbox */}
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => setAgreeToTerms(!agreeToTerms)}
              className={`w-6 h-6 rounded-md border flex-shrink-0 flex items-center justify-center transition-colors ${
                agreeToTerms 
                  ? 'bg-blue-500 border-blue-500' 
                  : 'border-white/20 hover:border-white/40'
              }`}
              data-testid="terms-checkbox"
            >
              {agreeToTerms && <Check weight="bold" className="w-4 h-4 text-white" />}
            </button>
            <label className="text-sm text-slate-400">
              I confirm that I am at least 18 years old and agree to the{' '}
              <a href="#" className="text-blue-500 hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-blue-500 hover:underline">Privacy Policy</a>.
              I understand this site contains adult content.
            </label>
          </div>
          
          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !dateOfBirth || !agreeToTerms}
            className="w-full py-4 bg-blue-500 text-white rounded-full font-semibold text-lg hover:bg-blue-600 transition-all btn-primary-glow disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="verify-age-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Verifying...
              </span>
            ) : (
              'Verify & Enter'
            )}
          </button>
        </form>
        
        {/* Footer */}
        <p className="text-xs text-slate-500 text-center mt-6">
          Your data is secure and will only be used for age verification purposes.
        </p>
      </motion.div>
    </div>
  );
};

export default AgeVerificationPage;
